import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Plus, Pencil, RefreshCw, Star, Rocket, Eye, Undo2, Radio } from "lucide-react";
import {
  adminGetLeads, adminUpdateLead, adminDeleteLead,
  adminGetProperties, adminDeleteProperty, logoutAdmin, localized, formatPrice,
  adminGetBlog, adminDeleteBlog, adminBlogSetStatus, resolveMediaUrl,
  adminIndexNowResubmit, adminIndexNowResubmitRecent,
} from "../../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Badge } from "../../components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { PropertyForm } from "./PropertyForm";
import { BlogForm } from "./BlogForm";
import { BlogReviewDialog } from "./BlogReviewDialog";
import ContentStudio from "./ContentStudio";
import { ContentStudioBoundary } from "./ContentStudioBoundary";

const STATUS_STYLES = {
  new: "bg-primary/15 text-primary border-primary/40",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/40",
  closed: "bg-muted text-muted-foreground border-border",
};

export default function AdminDashboard({ user }) {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [properties, setProperties] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [blogFormOpen, setBlogFormOpen] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  const [reviewBlog, setReviewBlog] = useState(null);     // draft being reviewed
  const [publishingId, setPublishingId] = useState("");   // id currently being published/unpublished
  const [confirm, setConfirm] = useState(null); // { type, id, label }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [l, p, b] = await Promise.all([adminGetLeads(), adminGetProperties(), adminGetBlog()]);
      setLeads(l);
      setProperties(p);
      setBlogPosts(b);
    } catch (e) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doLogout = async () => {
    try { await logoutAdmin(); } catch (e) { console.warn("Logout request failed:", e); }
    navigate("/admin/login", { replace: true });
  };

  const [pinging, setPinging] = useState(false);
  const runPing = async (mode) => {
    if (pinging) return;
    if (mode === "all" && !window.confirm(
      "Ping ALL published URLs (blog posts + properties + locale homepages)?\n\n" +
      "This is only recommended for one-off backfills — e.g. after Bing " +
      "verification propagates or after a large content overhaul. For regular use, " +
      "'Ping Recent' is the Bing/Yandex best-practice pattern."
    )) return;
    setPinging(true);
    try {
      const r = mode === "all"
        ? await adminIndexNowResubmit()
        : await adminIndexNowResubmitRecent(7);
      const engines = r?.engines || {};
      const accepted = r?.accepted || [];
      const scope = mode === "all"
        ? `${r.count} URLs (all)`
        : `${r.count} URLs · ${r.blog_posts || 0} posts + ${r.properties || 0} properties in last ${r.window_days}d`;
      if (accepted.length) {
        toast.success(
          `IndexNow: ${scope} · accepted by ${accepted.join(", ")}` +
          (r.hint ? ` — ${r.hint}` : "")
        );
      } else if (r.hint) {
        toast.error(`IndexNow: ${r.hint}`);
      } else {
        toast.error(
          `IndexNow rejected — Bing: ${engines.bing ?? "n/a"}, Yandex: ${engines.yandex ?? "n/a"}`
        );
      }
    } catch (e) {
      toast.error("IndexNow ping failed");
    } finally {
      setPinging(false);
    }
  };
  const doIndexNowResubmit = () => runPing("recent");
  const doIndexNowResubmitAll = () => runPing("all");

  const changeStatus = async (id, status) => {
    try {
      await adminUpdateLead(id, status);
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch (e) { toast.error("Update failed"); }
  };

  const runDelete = async () => {
    if (!confirm) return;
    try {
      if (confirm.type === "lead") {
        await adminDeleteLead(confirm.id);
        setLeads((ls) => ls.filter((l) => l.id !== confirm.id));
      } else if (confirm.type === "blog") {
        await adminDeleteBlog(confirm.id);
        setBlogPosts((bs) => bs.filter((b) => b.id !== confirm.id));
      } else {
        await adminDeleteProperty(confirm.id);
        setProperties((ps) => ps.filter((p) => p.id !== confirm.id));
      }
      toast.success("Deleted");
    } catch (e) { toast.error("Delete failed"); }
    finally { setConfirm(null); }
  };

  const openNew = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (p) => { setEditing(p); setFormOpen(true); };
  const onSaved = () => { setFormOpen(false); setEditing(null); load(); };
  const openNewBlog = () => { setEditingBlog(null); setBlogFormOpen(true); };
  const openEditBlog = (b) => { setEditingBlog(b); setBlogFormOpen(true); };
  const onBlogSaved = () => { setBlogFormOpen(false); setEditingBlog(null); load(); };

  const setBlogStatus = async (b, nextStatus) => {
    setPublishingId(b.id);
    try {
      await adminBlogSetStatus(b.id, { status: nextStatus });
      setBlogPosts((posts) => posts.map((p) => (p.id === b.id ? { ...p, status: nextStatus } : p)));
      setReviewBlog((rb) => (rb && rb.id === b.id ? { ...rb, status: nextStatus } : rb));
      toast.success(nextStatus === "published" ? "Post yayınlandı" : `Status: ${nextStatus}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Durum güncellenemedi");
    } finally {
      setPublishingId("");
    }
  };

  return (
    <div
      data-testid="admin-dashboard"
      translate="no"
      className="min-h-screen bg-background text-foreground notranslate"
    >
      {/* Top bar */}
      <header className="border-b border-border/60 bg-secondary">
        <div className="px-6 md:px-10 py-5 mx-auto max-w-[1400px] flex items-center justify-between">
          <div className="flex flex-col leading-none">
            <span className="font-headings text-2xl tracking-wide">Velmira</span>
            <span lang="en" className="brand-caps font-accent text-[10px] uppercase tracking-[0.35em] text-primary -mt-0.5">Living · Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden sm:block font-body text-sm text-foreground/60" data-testid="admin-user-email">{user?.email}</span>
            <button onClick={load} data-testid="admin-refresh" className="p-2 text-foreground/60 hover:text-primary transition-colors" aria-label="Refresh"><RefreshCw size={18} /></button>
            <button
              onClick={doIndexNowResubmit}
              disabled={pinging}
              data-testid="admin-indexnow-resubmit"
              className="hidden md:inline-flex items-center gap-2 border border-border/60 hover:border-primary hover:text-primary px-4 py-2 uppercase tracking-widest text-xs transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              title="Ping only URLs changed in the last 7 days (Bing/Yandex best practice)"
            >
              <Radio size={14} className={pinging ? "animate-pulse" : ""} />
              {pinging ? "Pinging…" : "Ping Recent (7d)"}
            </button>
            <button
              onClick={doIndexNowResubmitAll}
              disabled={pinging}
              data-testid="admin-indexnow-resubmit-all"
              className="hidden md:inline-flex items-center text-[10px] uppercase tracking-widest text-muted-foreground hover:text-primary underline underline-offset-4 disabled:opacity-50 disabled:cursor-not-allowed"
              title="One-off: ping ALL URLs. Use only for backfills."
            >
              all
            </button>
            <button onClick={doLogout} data-testid="admin-logout" className="inline-flex items-center gap-2 border border-border/60 hover:border-primary hover:text-primary px-4 py-2 uppercase tracking-widest text-xs transition-all"><LogOut size={14} /> Logout</button>
          </div>
        </div>
      </header>

      <main className="px-6 md:px-10 py-10 mx-auto max-w-[1400px]">
        <Tabs defaultValue="leads">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="leads" data-testid="tab-leads">Leads ({leads.length})</TabsTrigger>
            <TabsTrigger value="properties" data-testid="tab-properties">Properties ({properties.length})</TabsTrigger>
            <TabsTrigger value="blog" data-testid="tab-blog">Blog ({blogPosts.length})</TabsTrigger>
            <TabsTrigger value="studio" data-testid="tab-studio">AI Content Studio</TabsTrigger>
          </TabsList>

          {/* LEADS */}
          <TabsContent value="leads" className="mt-8">
            {loading ? (
              <p className="text-foreground/60 font-body">Loading…</p>
            ) : leads.length === 0 ? (
              <p data-testid="leads-empty" className="text-foreground/60 font-body">No leads yet.</p>
            ) : (
              <div className="flex flex-col gap-4" data-testid="leads-list">
                {leads.map((l) => (
                  <div key={l.id} data-testid={`lead-row-${l.id}`} className="border border-border/60 bg-secondary p-5 flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-3">
                        <span className="font-headings text-lg text-foreground">{l.name}</span>
                        <Badge variant="outline" className="uppercase text-[10px] tracking-wider">{l.interest}</Badge>
                        {l.source === "chat" ? <Badge variant="outline" className="uppercase text-[10px] tracking-wider border-primary/40 text-primary">via chat</Badge> : null}
                        {l.source === "buyer-brief" ? <Badge variant="outline" data-testid={`lead-buyer-brief-${l.id}`} className="uppercase text-[10px] tracking-wider border-primary/40 text-primary">buyer brief</Badge> : null}
                        {l.source === "owner-brief" ? <Badge variant="outline" data-testid={`lead-owner-brief-${l.id}`} className="uppercase text-[10px] tracking-wider border-primary/40 text-primary">owner brief</Badge> : null}
                        {l.source === "investor-brief" ? <Badge variant="outline" data-testid={`lead-investor-brief-${l.id}`} className="uppercase text-[10px] tracking-wider border-primary/40 text-primary">investor brief</Badge> : null}
                        <Badge variant="outline" className={`uppercase text-[10px] tracking-wider ${STATUS_STYLES[l.status] || ""}`}>{l.status}</Badge>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 font-body text-sm text-foreground/70">
                        <a href={`mailto:${l.email}`} className="hover:text-primary">{l.email}</a>
                        {l.phone ? <span>{l.phone}</span> : null}
                        {l.property_id ? <span className="text-muted-foreground">Property: {l.property_id}</span> : null}
                        <span className="text-muted-foreground">{new Date(l.created_at).toLocaleString()}</span>
                      </div>
                      <p className="mt-3 font-body font-light text-foreground/80 leading-relaxed whitespace-pre-wrap">{l.message}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <Select value={l.status} onValueChange={(v) => changeStatus(l.id, v)}>
                        <SelectTrigger data-testid={`lead-status-${l.id}`} className="w-36 bg-background border-border/60"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      <button onClick={() => setConfirm({ type: "lead", id: l.id, label: l.name })} data-testid={`lead-delete-${l.id}`} className="p-2.5 border border-border/60 text-foreground/60 hover:text-red-400 hover:border-red-400/50 transition-colors" aria-label="Delete lead"><Trash2 size={16} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* PROPERTIES */}
          <TabsContent value="properties" className="mt-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-headings text-xl text-foreground">Manage Properties</h2>
              <button onClick={openNew} data-testid="property-add" className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 uppercase tracking-[0.2em] text-xs font-body transition-all"><Plus size={15} /> Add Property</button>
            </div>
            {loading ? (
              <p className="text-foreground/60 font-body">Loading…</p>
            ) : properties.length === 0 ? (
              <p data-testid="properties-empty-admin" className="text-foreground/60 font-body">No properties yet.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="admin-properties-list">
                {properties.map((p) => (
                  <div key={p.id} data-testid={`admin-property-${p.id}`} className="border border-border/60 bg-secondary overflow-hidden flex flex-col">
                    <div className="aspect-[16/10] overflow-hidden bg-background">
                      {p.images?.[0] ? <img src={p.images[0]} alt={localized(p.title, "en")} className="w-full h-full object-cover" /> : null}
                    </div>
                    <div className="p-5 flex flex-col gap-2 flex-1">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="uppercase text-[10px] tracking-wider">{p.listing_type}</Badge>
                        {p.featured ? <Badge variant="outline" className="uppercase text-[10px] tracking-wider border-primary/40 text-primary"><Star size={10} className="mr-1" /> Featured</Badge> : null}
                      </div>
                      <h3 className="font-headings text-lg text-foreground leading-snug">{localized(p.title, "en")}</h3>
                      <p className="font-body text-sm text-muted-foreground">{p.location}</p>
                      <p className="font-body text-primary">{formatPrice(p.price, p.price_unit)}{p.listing_type === "rental" ? " / night" : p.listing_type === "mid_term" ? " / month" : ""}</p>
                      <div className="mt-auto flex items-center gap-2 pt-3">
                        <button onClick={() => openEdit(p)} data-testid={`property-edit-${p.id}`} className="inline-flex items-center gap-1.5 border border-border/60 hover:border-primary hover:text-primary px-4 py-2 text-xs uppercase tracking-widest transition-all"><Pencil size={13} /> Edit</button>
                        <button onClick={() => setConfirm({ type: "property", id: p.id, label: localized(p.title, "en") })} data-testid={`property-delete-${p.id}`} className="p-2 border border-border/60 text-foreground/60 hover:text-red-400 hover:border-red-400/50 transition-colors" aria-label="Delete property"><Trash2 size={15} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* BLOG */}
          <TabsContent value="blog" className="mt-8">
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-headings text-xl text-foreground">Manage Blog</h2>
              <button onClick={openNewBlog} data-testid="blog-add" className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 uppercase tracking-[0.2em] text-xs font-body transition-all"><Plus size={15} /> New Post</button>
            </div>
            {loading ? (
              <p className="text-foreground/60 font-body">Loading…</p>
            ) : blogPosts.length === 0 ? (
              <p data-testid="blog-empty-admin" className="text-foreground/60 font-body">No blog posts yet. Click &ldquo;New Post&rdquo; to write one in Turkish — it will be auto-translated to English &amp; French.</p>
            ) : (
              <div className="flex flex-col gap-4" data-testid="admin-blog-list">
                {blogPosts.map((b) => (
                  <div key={b.id} data-testid={`admin-blog-${b.id}`} className="border border-border/60 bg-secondary p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                    {b.image ? <img src={resolveMediaUrl(b.image)} alt="" className="w-full sm:w-28 h-28 object-cover shrink-0" /> : null}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-headings text-lg text-foreground leading-snug">{localized(b.title, "tr") || localized(b.title, "en")}</h3>
                      <p className="mt-1 font-body text-sm text-muted-foreground truncate">/{b.slug}</p>
                      <p className="mt-1 font-body text-xs text-muted-foreground">{b.date} · {b.author}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5 items-center">
                        {["en", "fr", "tr"].map((l) => (
                          <Badge key={l} variant="outline" className="uppercase text-[10px] tracking-wider">{l}</Badge>
                        ))}
                        {b.category ? <Badge variant="outline" className="uppercase text-[10px] tracking-wider border-primary/40 text-primary">{b.category}</Badge> : null}
                        {b.status && b.status !== "published" ? <Badge variant="outline" className="uppercase text-[10px] tracking-wider border-yellow-500/40 text-yellow-300">{b.status}</Badge> : null}
                        {b.scheduled_at ? <Badge variant="outline" className="uppercase text-[10px] tracking-wider border-blue-500/40 text-blue-300">scheduled</Badge> : null}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0 flex-wrap">
                      <button onClick={() => setReviewBlog(b)} data-testid={`blog-review-${b.id}`} className="inline-flex items-center gap-1.5 border border-border/60 hover:border-primary hover:text-primary px-4 py-2 text-xs uppercase tracking-widest transition-all"><Eye size={13} /> Review</button>
                      {b.status !== "published" ? (
                        <button
                          onClick={() => setBlogStatus(b, "published")}
                          disabled={publishingId === b.id}
                          data-testid={`blog-publish-${b.id}`}
                          className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-4 py-2 text-xs uppercase tracking-widest transition-all"
                        >
                          <Rocket size={13} /> {publishingId === b.id ? "Yayınlanıyor…" : "Publish"}
                        </button>
                      ) : (
                        <button
                          onClick={() => setBlogStatus(b, "draft")}
                          disabled={publishingId === b.id}
                          data-testid={`blog-unpublish-${b.id}`}
                          className="inline-flex items-center gap-1.5 border border-border/60 text-foreground/70 hover:border-yellow-500/50 hover:text-yellow-300 px-4 py-2 text-xs uppercase tracking-widest transition-all"
                        >
                          <Undo2 size={13} /> Unpublish
                        </button>
                      )}
                      <button onClick={() => openEditBlog(b)} data-testid={`blog-edit-${b.id}`} className="inline-flex items-center gap-1.5 border border-border/60 hover:border-primary hover:text-primary px-4 py-2 text-xs uppercase tracking-widest transition-all"><Pencil size={13} /> Edit</button>
                      <button onClick={() => setConfirm({ type: "blog", id: b.id, label: localized(b.title, "tr") || localized(b.title, "en") })} data-testid={`blog-delete-${b.id}`} className="p-2 border border-border/60 text-foreground/60 hover:text-red-400 hover:border-red-400/50 transition-colors" aria-label="Delete post"><Trash2 size={15} /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* AI CONTENT STUDIO */}
          <TabsContent value="studio" className="mt-8" data-testid="tab-content-studio">
            <ContentStudioBoundary>
              <ContentStudio onSaved={load} />
            </ContentStudioBoundary>
          </TabsContent>
        </Tabs>
      </main>

      {/* Property form dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="bg-card border-border/60 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-headings text-2xl">{editing ? "Edit Property" : "Add Property"}</DialogTitle>
            <DialogDescription>Content is multilingual — fill EN, FR and TR fields. English title is required.</DialogDescription>
          </DialogHeader>
          <PropertyForm initial={editing} onSaved={onSaved} onCancel={() => setFormOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Blog form dialog */}
      <Dialog open={blogFormOpen} onOpenChange={setBlogFormOpen}>
        <DialogContent className="bg-card border-border/60 max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-headings text-2xl">{editingBlog ? "Edit Post" : "New Post"}</DialogTitle>
            <DialogDescription>Write in Turkish. English & French translations are generated automatically when you save.</DialogDescription>
          </DialogHeader>
          <BlogForm initial={editingBlog} onSaved={onBlogSaved} onCancel={() => setBlogFormOpen(false)} />
        </DialogContent>
      </Dialog>

      {/* Blog review dialog (read-only preview + Publish action) */}
      <BlogReviewDialog
        post={reviewBlog}
        onClose={() => setReviewBlog(null)}
        onPublish={(b) => setBlogStatus(b, "published")}
        onUnpublish={(b) => setBlogStatus(b, "draft")}
        publishing={publishingId === (reviewBlog?.id || "")}
      />

      {/* Delete confirmation */}
      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent className="bg-card border-border/60">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {confirm?.type}?</AlertDialogTitle>
            <AlertDialogDescription>
              &ldquo;{confirm?.label}&rdquo; will be permanently removed. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={runDelete} data-testid="delete-confirm" className="bg-red-500 hover:bg-red-600 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
